import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import {
  useBiteStatusStore,
  BiteStatus,
  BITE_STATUS,
} from "@/features/tutor/store/useBiteStatusStore";
import { ASSESSMENT_KEYS } from "@/features/tutor/constants/assessmentTypes";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { captureAndUploadSlideScreenshot } from "@/features/tutor/utils/slideScreenshot";
import { persistNotepadCheckpoint } from "@/features/notepad/components/canvasApi";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore";
import { useForm } from "@/features/tutor/context/FormContext";
import { CHARACTER_MAP } from "@/constants/character";
import { useNotepadPerSlideStore } from "@/features/notepad/store/useNotepadPerSlideStore";
import { useIsSlideNavigationLocked } from "@/features/tutor/hooks/slideNavigationLockHook";

const ASSESSMENT_TYPE_SET = new Set(ASSESSMENT_KEYS);

const SLIDE_PROGRESS_STYLES = `
  @keyframes spPulseLight { 0%,100%{opacity:1} 50%{opacity:0.4} }
`;

const isAssessmentSlide = (s: {
  metadata?: { slide_type?: string };
  slide_type?: string;
}) => {
  const t = s.metadata?.slide_type ?? (s as any).slide_type;
  return t != null && ASSESSMENT_TYPE_SET.has(t);
};

// Color used for the connector lines, derived from status
function getStatusLineColor(
  status: BiteStatus,
  assessmentCompleted?: boolean,
): string {
  if (status === BITE_STATUS.ASSESSMENT)
    return assessmentCompleted ? "#68B85D" : "#E9E9E9";
  switch (status) {
    case BITE_STATUS.COMPLETED:
      return "#68B85D";
    case BITE_STATUS.INCOMPLETE:
      return "#FABC00";
    case BITE_STATUS.CURRENTLY_LEARNING:
      return "#FABC00";
    case BITE_STATUS.PROCESSING:
      return "#E9E9E9";
    case BITE_STATUS.SKIPPED_BY_USER:
    case BITE_STATUS.SKIPPED_BY_AI:
    case BITE_STATUS.NOT_STARTED:
      return "#E9E9E9";
    default:
      return "#E9E9E9";
  }
}

function StatusCircle({
  status,
  isAssessment,
  assessmentCompleted,
  voiceIcon,
}: {
  status: BiteStatus;
  isAssessment: boolean;
  assessmentCompleted?: boolean;
  voiceIcon?: string;
}) {
  if (isAssessment) {
    return (
      <img
        src={
          assessmentCompleted
            ? "/assets/page-assessment-green.svg"
            : "/assets/page-assessment-grey.svg"
        }
        style={{ width: 34, height: 34 }}
        alt={
          assessmentCompleted
            ? "Assessment (completed)"
            : "Assessment (not started)"
        }
        className="shrink-0"
      />
    );
  }
  switch (status) {
    case BITE_STATUS.CURRENTLY_LEARNING:
      return voiceIcon ? (
        <img
          src={voiceIcon}
          width={24}
          height={24}
          alt="Currently learning"
          className="shrink-0 rounded-full"
        />
      ) : (
        <img
          src="/assets/bite-learning-alt.svg"
          width={24}
          height={24}
          alt="Currently learning"
          className="shrink-0"
        />
      );
    case BITE_STATUS.COMPLETED:
      return (
        <img
          src="/assets/bite-mastered.svg"
          width={24}
          height={24}
          alt="Completed"
          className="shrink-0"
        />
      );
    case BITE_STATUS.INCOMPLETE:
      return (
        <img
          src="/assets/bite-in-progress.svg"
          width={24}
          height={24}
          alt="Incomplete"
          className="shrink-0"
        />
      );
    case BITE_STATUS.NOT_STARTED:
      return (
        <img
          src="/assets/bite-not-started-or-skipped-user.svg"
          width={24}
          height={24}
          alt="Not started"
          className="shrink-0"
          style={{ opacity: 0.3 }}
        />
      );
    case BITE_STATUS.SKIPPED_BY_USER:
      return (
        <img
          src="/assets/bite-not-started-or-skipped-user.svg"
          width={24}
          height={24}
          alt="Skipped by user"
          className="shrink-0"
        />
      );
    case BITE_STATUS.SKIPPED_BY_AI:
      return (
        <img
          src="/assets/bite-skipped-ai.svg"
          width={24}
          height={24}
          alt="Skipped by AI"
          className="shrink-0"
        />
      );
    case BITE_STATUS.PROCESSING:
      return (
        <div className="relative w-[24px] h-[24px] shrink-0 flex items-center justify-center">
          {/* Spinning border */}
          <div
            className="absolute inset-0 rounded-full animate-spin"
            style={{
              animationDuration: "2s",
              background:
                "conic-gradient(#7B6EE6 0deg, #7B6EE6 120deg, transparent 120deg)",
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 1.5px), white calc(100% - 1.5px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 1.5px), white calc(100% - 1.5px))",
            }}
          />
          {/* Icon background + magnifier */}
          <div
            className="w-[20px] h-[20px] rounded-full flex items-center justify-center"
            style={{ background: "#F7F6FF" }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <circle
                cx="5"
                cy="5"
                r="3.25"
                stroke="#7B6EE6"
                strokeWidth="1.3"
              />
              <line
                x1="7.5"
                y1="7.5"
                x2="10.5"
                y2="10.5"
                stroke="#7B6EE6"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      );
    default:
      return (
        <img
          src="/assets/bite-not-started-or-skipped-user.svg"
          width={24}
          height={24}
          alt="Not started"
          className="shrink-0"
          style={{ opacity: 0.3 }}
        />
      );
  }
}

const MAX_VISIBLE = 8;
const ROW_H_BITE = 28;
const ROW_H_ASSESSMENT = 40;
const CONNECTOR_H = 12;
const DROPDOWN_PADDING_H = 24;

const SlideProgress = ({
  currentSlideIndex,
  slides,
}: {
  currentSlideIndex: number;
  slides?: Array<{
    metadata?: { slide_type?: string };
    slide_type?: string;
    slide_index?: number;
  }>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number>(
    (ROW_H_BITE + CONNECTOR_H) * MAX_VISIBLE + DROPDOWN_PADDING_H,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The panel renders in a portal on <body>, so it is positioned in viewport
  // coordinates taken from the trigger rather than relative to its wrapper.
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{
    left: number;
    bottom: number;
  } | null>(null);

  const positionPanel = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const sorted = [...(slides ?? [])].sort(
      (a, b) => (a.slide_index ?? 0) - (b.slide_index ?? 0),
    );
    const visible = sorted.slice(0, MAX_VISIBLE);
    const naturalH =
      visible.reduce((acc, s, i, arr) => {
        const t = (s as any).metadata?.slide_type ?? (s as any).slide_type;
        const rowH =
          t != null && ASSESSMENT_TYPE_SET.has(t)
            ? ROW_H_ASSESSMENT
            : ROW_H_BITE;
        return acc + rowH + (i < arr.length - 1 ? CONNECTOR_H : 0);
      }, 0) + DROPDOWN_PADDING_H;
    setDropdownMaxHeight(Math.min(rect.top - 20, naturalH));
    setPanelPos({
      left: rect.left,
      bottom: window.innerHeight - rect.top + 8,
    });
  }, [slides]);

  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      positionPanel();
      setIsMounted(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      closeTimerRef.current = setTimeout(() => setIsMounted(false), 200);
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isOpen, positionPanel]);

  useEffect(() => {
    setIsOpen(false);
  }, [currentSlideIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => requestAnimationFrame(positionPanel);
    window.addEventListener("resize", reposition);
    window.addEventListener("orientationchange", reposition);
    screen.orientation?.addEventListener("change", reposition);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("orientationchange", reposition);
      screen.orientation?.removeEventListener("change", reposition);
    };
  }, [isOpen, positionPanel]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const { formData } = useForm();
  const voiceIcon = formData.character?.characterId
    ? CHARACTER_MAP[formData.character.characterId]?.avatar
    : undefined;

  const handleChangeSlide = useSlideStore((s) => s.handleChangeSlide);
  const runManualSlideChange = useSlideStore((s) => s.runManualSlideChange);
  const isNavigationLocked = useIsSlideNavigationLocked();
  const sessionId = useSessionStore((s) => s.sessionId);
  const statuses = useBiteStatusStore((s) => s.statuses);
  const isInitialized = useBiteStatusStore((s) => s.isInitialized);
  const assessments = useAssessmentStore((s) => s.assessments);
  const notepadFlags = useNotepadPerSlideStore((s) => s.flags);
  const { emit } = useRuntimeContext();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      // The panel is portalled out, so it is not inside dropdownRef and has to
      // be tested separately — otherwise clicking a row closes the menu before
      // the row's own handler runs.
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !panelRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const allSlides = [...(slides ?? [])].sort(
    (a, b) => (a.slide_index ?? 0) - (b.slide_index ?? 0),
  );
  const contentSlides = allSlides.filter((s) => !isAssessmentSlide(s));
  const contentCount = contentSlides.length;

  if (!contentCount) {
    return (
      <>
        <style>{SLIDE_PROGRESS_STYLES}</style>
        <div
          style={{
            height: 48,
            width: 155,
            borderRadius: 12,
            background: "#1A1A2E",
            animation: "spPulseLight 1.4s ease-in-out infinite",
          }}
        />
      </>
    );
  }

  const currentPosition =
    contentSlides.findIndex((s) => s.slide_index === currentSlideIndex) + 1 ||
    1;

  const firstIsAssessment =
    allSlides.length > 0 && isAssessmentSlide(allSlides[0]);
  const lastIsAssessment =
    allSlides.length > 0 && isAssessmentSlide(allSlides[allSlides.length - 1]);

  // Resolve statuses for all slides (used for connector line colors)
  const resolvedStatuses: BiteStatus[] = allSlides.map((slide) => {
    const assessment = isAssessmentSlide(slide);
    return (
      statuses[slide.slide_index ?? -1] ??
      (assessment ? BITE_STATUS.ASSESSMENT : BITE_STATUS.NOT_STARTED)
    );
  });

  return (
    <div ref={dropdownRef}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((v) => !v)}
        style={{ fontFamily: "Nunito, sans-serif", height: 48, width: 155 }}
        className={`flex items-center gap-2 px-3 rounded-xl text-sm font-medium whitespace-nowrap focus:outline-none transition-colors ${
          isOpen
            ? "bg-white text-gray-900 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            : "bg-[#1A1A2E] text-white"
        }`}
      >
        <img
          src="/assets/bite-learning.svg"
          width={26}
          height={26}
          alt=""
          className="shrink-0"
        />
        <span>
          Bite {currentPosition} of {contentCount}
        </span>
        <svg
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          className={`transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
        >
          <path
            d="M1 7L6 2L11 7"
            stroke={isOpen ? "#1A1A2E" : "white"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Dropdown panel — opens upward.
          Rendered through a portal on <body>. DesktopBottomBar wraps the left
          controls in an `overflow-hidden` box (so a wide cluster can't slide
          under the centred speak button). This panel is drawn entirely above
          the trigger, i.e. outside that box, so it was being clipped away
          completely. Portalling puts it outside the clipping subtree; the box
          keeps doing its job for everything that really is inside it. */}
      {isMounted &&
        panelPos &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              bottom: panelPos.bottom,
              left: panelPos.left,
              overflowY: "auto",
              maxHeight: `${dropdownMaxHeight}px`,
              boxShadow: "0px 0px 5px 2px rgba(0,0,0,0.05)",
              fontFamily: "Nunito, sans-serif",
              fontWeight: 600,
              width: 155,
              transformOrigin: "bottom left",
              transform: isVisible ? "translateY(0)" : "translateY(12px)",
              opacity: isVisible ? 1 : 0,
              transition:
                "transform 180ms cubic-bezier(0.22, 1, 0.36, 1), opacity 150ms ease",
            }}
            className={`bg-white rounded-2xl ${firstIsAssessment ? "pt-1.5" : "pt-3"} ${lastIsAssessment ? "pb-1.5" : "pb-3"} z-50`}
          >
            {!isInitialized ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {allSlides.slice(0, MAX_VISIBLE).map((slide, i, arr) => {
                  const assessment = isAssessmentSlide(slide);
                  const rowH = assessment ? ROW_H_ASSESSMENT : ROW_H_BITE;
                  const iconSize = assessment ? 34 : 24;
                  return (
                    <div
                      key={i}
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          height: rowH,
                          paddingLeft: 8,
                          paddingRight: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 34,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              width: iconSize,
                              height: iconSize,
                              borderRadius: assessment ? 4 : "50%",
                              background: "#f0f0f0",
                              animation:
                                "spPulseLight 1.4s ease-in-out infinite",
                              animationDelay: `${i * 0.1}s`,
                            }}
                          />
                        </div>
                        {!assessment && (
                          <div
                            style={{
                              height: 10,
                              borderRadius: 5,
                              background: "#f0f0f0",
                              flex: 1,
                              animation:
                                "spPulseLight 1.4s ease-in-out infinite",
                              animationDelay: `${i * 0.1 + 0.05}s`,
                            }}
                          />
                        )}
                      </div>
                      {i < arr.length - 1 && (
                        <div
                          style={{
                            height: CONNECTOR_H,
                            paddingLeft: 8,
                            display: "flex",
                          }}
                        >
                          <div
                            style={{
                              width: 34,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                width: 2,
                                height: "100%",
                                background: "#f0f0f0",
                                borderRadius: 1,
                                animation:
                                  "spPulseLight 1.4s ease-in-out infinite",
                                animationDelay: `${i * 0.1 + 0.1}s`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <style>{SLIDE_PROGRESS_STYLES}</style>
              </div>
            ) : (
              allSlides.map((slide, idx) => {
                const assessment = isAssessmentSlide(slide);
                const slideType =
                  (slide as any).metadata?.slide_type ??
                  (slide as any).slide_type;
                const assessmentSlice =
                  assessment && slideType ? assessments[slideType] : null;
                const assessmentCompleted = assessmentSlice
                  ? assessmentSlice.completed === true ||
                    assessmentSlice.state === "completed"
                  : false;
                const hasNotepadUsed =
                  !assessment && notepadFlags[slide.slide_index ?? -1] === true;
                const contentPos = assessment
                  ? null
                  : contentSlides.findIndex(
                      (s) => s.slide_index === slide.slide_index,
                    ) + 1;
                const label = assessment ? null : `Bite ${contentPos}`;
                const isActive = slide.slide_index === currentSlideIndex;
                const status: BiteStatus = isActive
                  ? BITE_STATUS.CURRENTLY_LEARNING
                  : resolvedStatuses[idx];

                const hasNext = idx < allSlides.length - 1;
                const nextSlide = hasNext ? allSlides[idx + 1] : null;
                const nextIsActive =
                  nextSlide?.slide_index === currentSlideIndex;
                const nextStatus: BiteStatus | null = hasNext
                  ? nextIsActive
                    ? BITE_STATUS.CURRENTLY_LEARNING
                    : resolvedStatuses[idx + 1]
                  : null;
                const nextSlideType = nextSlide
                  ? ((nextSlide as any).metadata?.slide_type ??
                    (nextSlide as any).slide_type)
                  : null;
                const nextAssessmentCompleted =
                  nextSlide && isAssessmentSlide(nextSlide) && nextSlideType
                    ? assessments[nextSlideType]?.completed === true ||
                      assessments[nextSlideType]?.state === "completed"
                    : false;
                const NOT_STARTED_COLOR = "rgba(233,233,233,0.3)";
                const lineColorTop =
                  status === BITE_STATUS.NOT_STARTED ||
                  nextStatus === BITE_STATUS.NOT_STARTED
                    ? NOT_STARTED_COLOR
                    : getStatusLineColor(status, assessmentCompleted);
                const lineColorBottom =
                  status === BITE_STATUS.NOT_STARTED ||
                  nextStatus === BITE_STATUS.NOT_STARTED
                    ? NOT_STARTED_COLOR
                    : nextStatus
                      ? getStatusLineColor(nextStatus, nextAssessmentCompleted)
                      : lineColorTop;

                // Layout constants
                // icon column: 34px wide, paddingLeft 8px → icon center at 8 + 17 = 25px from container left
                // bypass line runs at X = 6px (left of icon)
                const ICON_CENTER_X = 25;
                const ICON_RADIUS = 12;
                const BYPASS_X = 6;
                const rowH = assessment ? ROW_H_ASSESSMENT : ROW_H_BITE;
                const nextRowH = nextSlide
                  ? isAssessmentSlide(nextSlide)
                    ? ROW_H_ASSESSMENT
                    : ROW_H_BITE
                  : ROW_H_BITE;

                const isSkippedAI = status === BITE_STATUS.SKIPPED_BY_AI;
                const nextIsSkippedAI =
                  nextStatus === BITE_STATUS.SKIPPED_BY_AI;

                // Each connector SVG spans: top half of current row + gap + top half of next row
                // so the line visually connects the center of one icon to the center of the next.
                // SVG is placed in the gap div (height=CONNECTOR_H) but overflows into adjacent rows via overflow:visible.
                // SVG coordinate system: y=0 is top of gap div.
                //   → icon center above = -(rowH / 2)
                //   → icon center below = CONNECTOR_H + (nextRowH / 2)
                const aboveCenter = -(rowH / 2);
                const belowCenter = CONNECTOR_H + nextRowH / 2;
                const iconBottomY = aboveCenter + ICON_RADIUS; // bottom edge of current icon
                const iconTopY = belowCenter - ICON_RADIUS; // top edge of next icon
                const svgW = ICON_CENTER_X + 4;

                return (
                  <div
                    key={slide.slide_index}
                    className="flex flex-col items-start w-full"
                  >
                    {/* Row */}
                    <div
                      onClick={() => {
                        if (
                          assessment ||
                          status === BITE_STATUS.NOT_STARTED ||
                          isActive ||
                          isNavigationLocked
                        )
                          return;
                        runManualSlideChange(async () => {
                          await captureAndUploadSlideScreenshot(
                            currentSlideIndex,
                          );
                          // Jumping away from the CURRENT slide, which may be a
                          // notepad bite whose tldraw snapshot is client-owned
                          // and has no other delivery path. No-op elsewhere.
                          await persistNotepadCheckpoint();
                          emit("TURN_CHANGE");
                          setIsOpen(false);
                          return handleChangeSlide(
                            sessionId,
                            slide.slide_index,
                            "user" as any,
                          );
                        });
                      }}
                      className={`flex items-center gap-1 px-2 w-full ${assessment || status === BITE_STATUS.NOT_STARTED || isActive || isNavigationLocked ? "cursor-default" : "cursor-pointer"}`}
                      style={{ height: `${rowH}px` }}
                    >
                      {/* Icon column */}
                      <div
                        className="flex items-center justify-center shrink-0"
                        style={{ width: 34 }}
                      >
                        <StatusCircle
                          status={status}
                          isAssessment={assessment}
                          assessmentCompleted={assessmentCompleted}
                          voiceIcon={voiceIcon}
                        />
                      </div>

                      {/* Label + scratchpad icon */}
                      {label !== null && (
                        <div className="flex items-center justify-between w-full pr-2">
                          <span
                            className={`text-sm truncate rounded-md py-0.5 transition-colors ${
                              isActive
                                ? "font-semibold text-gray-900"
                                : "text-gray-500"
                            } ${status === BITE_STATUS.NOT_STARTED ? "opacity-30" : ""} ${
                              !assessment &&
                              status !== BITE_STATUS.NOT_STARTED &&
                              !isActive &&
                              !isNavigationLocked
                                ? "hover:bg-[#F4F4F4]"
                                : ""
                            }`}
                          >
                            {label}
                          </span>
                          {hasNotepadUsed && (
                            <img
                              src="/assets/marker.svg"
                              width={18}
                              height={18}
                              alt="Notepad used"
                              className="shrink-0"
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Connector between this row and the next */}
                    {hasNext &&
                      (() => {
                        // Case A: current→next is a bypass entry: normal slide → skipped_by_ai
                        // Path: from icon center above → curves to bypass X at center of next row
                        if (!isSkippedAI && nextIsSkippedAI) {
                          return (
                            <div
                              style={{
                                height: CONNECTOR_H,
                                position: "relative",
                              }}
                              className="w-full"
                            >
                              <svg
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  overflow: "visible",
                                  pointerEvents: "none",
                                }}
                                width={svgW}
                                height={CONNECTOR_H}
                              >
                                <path
                                  d={`M ${ICON_CENTER_X} ${iconBottomY} C ${ICON_CENTER_X} ${(iconBottomY + belowCenter) / 2}, ${BYPASS_X} ${(iconBottomY + belowCenter) / 2}, ${BYPASS_X} ${belowCenter}`}
                                  stroke={lineColorTop}
                                  strokeWidth={2}
                                  strokeDasharray="4 3"
                                  strokeLinecap="round"
                                  fill="none"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Case B: skipped_by_ai → skipped_by_ai: straight line along bypass X
                        if (isSkippedAI && nextIsSkippedAI) {
                          return (
                            <div
                              style={{
                                height: CONNECTOR_H,
                                position: "relative",
                              }}
                              className="w-full"
                            >
                              <svg
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  overflow: "visible",
                                  pointerEvents: "none",
                                }}
                                width={svgW}
                                height={CONNECTOR_H}
                              >
                                <line
                                  x1={BYPASS_X}
                                  y1={aboveCenter}
                                  x2={BYPASS_X}
                                  y2={belowCenter}
                                  stroke={lineColorTop}
                                  strokeWidth={2}
                                  strokeDasharray="4 3"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Case C: skipped_by_ai → normal slide: curves from bypass X back to icon center
                        if (isSkippedAI && !nextIsSkippedAI) {
                          return (
                            <div
                              style={{
                                height: CONNECTOR_H,
                                position: "relative",
                              }}
                              className="w-full"
                            >
                              <svg
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  overflow: "visible",
                                  pointerEvents: "none",
                                }}
                                width={svgW}
                                height={CONNECTOR_H}
                              >
                                <path
                                  d={`M ${BYPASS_X} ${aboveCenter} C ${BYPASS_X} ${(aboveCenter + iconTopY) / 2}, ${ICON_CENTER_X} ${(aboveCenter + iconTopY) / 2}, ${ICON_CENTER_X} ${iconTopY}`}
                                  stroke={lineColorBottom}
                                  strokeWidth={2}
                                  strokeDasharray="4 3"
                                  strokeLinecap="round"
                                  fill="none"
                                />
                              </svg>
                            </div>
                          );
                        }

                        // Case D: normal straight connector
                        return (
                          <div
                            style={{ height: CONNECTOR_H, paddingLeft: 8 }}
                            className="w-full flex"
                          >
                            <div
                              style={{
                                width: 34,
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                style={{
                                  width: 2,
                                  height: "100%",
                                  background: `linear-gradient(to bottom, ${lineColorTop}, ${lineColorBottom})`,
                                  borderRadius: 1,
                                }}
                              />
                            </div>
                          </div>
                        );
                      })()}
                  </div>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SlideProgress;
